/**
 * One row of the Settings page (W2-13 settings-keymap; DESIGN.md §4.20): title, description, the editor and a
 * Reset button. Every change applies live (the settings service notifies every subscriber at once); Reset restores
 * the default of the row (one field of an object setting resets only that field). Rows of the Full level carry a
 * "Full" badge while the user is at the Guided level (disclosure never hides a setting). A changed row says so in
 * words next to its title (colour is never the only signal).
 *
 * Default editors: switch (boolean), number field (expressions allowed: `+=10`, `*2`), slider (percent, times,
 * logarithmic travel), segmented control or select (choices), text field. Rows contributed by feature modules may
 * bring their own component. The editor's accessible name is the row title.
 */

import { useCallback, useId, useMemo, useState, type ReactNode } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { UserSettings } from '../../app/settings/types';
import { Expr } from '../../core/expr';
import { assertNever } from '../../core/util/assert';
import type { Json } from '../../core/util/types';
import { Badge, IconButton, NumberField, Segmented, Select, Slider, Switch, TextField } from '../../ui';
import type { SettingsText } from './hooks';
import { m } from './settings.messages';
import { optionText, type ChoiceOption, type RowEditor, type RowItem } from './settingsCatalog';
import { deepEqual, fieldOf, isDefaultValue, resetSetting, valueAtPath, writeField } from './settingsValue';
import s from './Settings.module.css';

export interface SettingRowProps {
  readonly services: AppServices;
  readonly item: RowItem;
  /** Current settings (the row re-renders when they change). */
  readonly settings: UserSettings;
  readonly text: SettingsText;
  /** The user is at the Guided level (Full rows get a badge). */
  readonly guided: boolean;
  /** Briefly highlighted after a palette jump or a deep link. */
  readonly highlighted?: boolean;
}

/** Writes a row's value (its own writer, or the settings service), then its side effect. */
export function writeRow(services: AppServices, item: RowItem, value: unknown): void {
  if (item.write !== undefined) item.write(services, value);
  else writeField(services.settings, item.path, item.field, value);
  item.afterWrite?.(services, value);
}

/** Resets a row to its default (through its own writer when it has one). */
export function resetRow(services: AppServices, item: RowItem): void {
  if (item.write === undefined) {
    resetSetting(services.settings, item.path, item.field);
  } else {
    // Rows with their own writer (keyboard preset, Alt-free) reset through the service, then re-apply its value so
    // the owner (the keymap) sees the change too.
    resetSetting(services.settings, item.path, item.field);
    item.write(services, fieldOf(valueAtPath(services.settings.value, item.path), item.field));
  }
  item.afterWrite?.(services, fieldOf(valueAtPath(services.settings.value, item.path), item.field));
}

export function SettingRow(props: SettingRowProps) {
  const { services, item, text } = props;
  const { t } = text;
  const titleId = useId();
  const descId = useId();
  const title = t(item.title);
  const value = fieldOf(valueAtPath(props.settings, item.path), item.field);
  const isDefault = isDefaultValue(services.settings, item.path, item.field);
  const changed = isDefault === false;
  const write = useCallback(
    (v: unknown): void => {
      writeRow(services, item, v);
    },
    [services, item],
  );

  return (
    <div
      id={`setting-${item.id}`}
      className={s.row}
      data-setting={item.id}
      data-highlighted={props.highlighted === true ? 'true' : undefined}
      data-changed={changed ? 'true' : undefined}
      tabIndex={-1}
      role="group"
      aria-labelledby={titleId}
      aria-describedby={item.description === undefined ? undefined : descId}
    >
      <div className={s.rowText}>
        <div className={s.rowTitleLine}>
          <span id={titleId} className={s.rowTitle}>
            {title}
          </span>
          {item.level === 'full' && props.guided && <Badge kind="full" />}
          {changed && (
            <span className={s.changed} title={t(m.changedHint)}>
              {t(m.changed)}
            </span>
          )}
        </div>
        {item.description !== undefined && (
          <p id={descId} className={s.rowDesc}>
            {t(item.description)}
          </p>
        )}
      </div>
      <div className={s.rowControl}>
        <RowEditorView
          services={services}
          editor={item.editor}
          value={value}
          title={title}
          text={text}
          onChange={write}
          commitOnRelease={item.path === 'general.uiScale'}
        />
        {item.extra !== undefined && <item.extra services={services} />}
      </div>
      <div className={s.rowReset}>
        <IconButton
          icon="RotateCcw"
          size="sm"
          label={{ key: m.resetRow, params: { title } }}
          disabledReason={isDefault === true ? { key: m.resetAlreadyDefault } : null}
          onClick={() => {
            resetRow(services, item);
          }}
        />
      </div>
    </div>
  );
}

interface EditorViewProps {
  readonly services: AppServices;
  readonly editor: RowEditor;
  readonly value: unknown;
  readonly title: string;
  readonly text: SettingsText;
  onChange(v: unknown): void;
  /** Slider writes once on release (UI scale would otherwise resize the slider under the pointer). */
  readonly commitOnRelease?: boolean;
}

function hidden(label: string): ReactNode {
  return <span className="visually-hidden">{label}</span>;
}

/** The editor of a row, by kind. */
export function RowEditorView(props: EditorViewProps) {
  const { editor, value, title } = props;
  switch (editor.kind) {
    case 'boolean':
      return (
        <Switch
          label={hidden(title)}
          checked={value === true}
          onChange={(v) => {
            props.onChange(v);
          }}
        />
      );
    case 'number':
      return <NumberEditor {...props} editor={editor} />;
    case 'slider':
      return <SliderEditor {...props} editor={editor} />;
    case 'choice':
      return <ChoiceEditor {...props} editor={editor} />;
    case 'text':
      return (
        <TextField
          label={title}
          hideLabel
          value={typeof value === 'string' ? value : ''}
          {...(editor.maxLength === undefined ? {} : { maxLength: editor.maxLength })}
          {...(editor.placeholder === undefined ? {} : { placeholder: { key: editor.placeholder } })}
          onCommit={(v) => {
            props.onChange(v);
          }}
        />
      );
    case 'custom': {
      const Custom = editor.component;
      return <Custom services={props.services} />;
    }
    default:
      return assertNever(editor);
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Number
// ---------------------------------------------------------------------------------------------------------------

function NumberEditor(props: EditorViewProps & { readonly editor: Extract<RowEditor, { kind: 'number' }> }) {
  const { editor, title, text } = props;
  const scale = editor.scale ?? 1;
  const nullable = editor.nullable;
  const isNull = props.value === null || props.value === undefined;
  const stored = typeof props.value === 'number' ? props.value : (nullable?.onValue ?? 0);
  const shown = stored * scale;
  const clamp = (v: number): number => {
    const c = Math.min(editor.max, Math.max(editor.min, v / scale));
    return editor.integer === true ? Math.round(c) : c;
  };
  const field = (
    <NumberField
      className={s.number}
      stacked
      label={hidden(title)}
      value={{ kind: 'value', value: shown }}
      vars="field"
      min={editor.min * scale}
      max={editor.max * scale}
      {...(editor.step === undefined ? {} : { step: editor.step })}
      {...(editor.precision === undefined ? {} : { precision: editor.precision })}
      {...(editor.unit === undefined ? {} : { unit: optionText(text.t, editor.unit) })}
      onCommit={(c) => {
        if (c.kind === 'number') {
          props.onChange(clamp(c.value));
          return;
        }
        const out = Expr.apply(c.program, { v: shown, i: 0, n: 1, u: 0 }, Expr.rng(1));
        if (out !== null && Number.isFinite(out)) props.onChange(clamp(out));
      }}
    />
  );
  if (nullable === undefined) return field;
  return (
    <div className={s.nullable}>
      <Segmented<'off' | 'on'>
        ariaLabel={title}
        size="sm"
        value={isNull ? 'off' : 'on'}
        options={[
          { value: 'off', label: text.t(nullable.offLabel) },
          { value: 'on', label: text.t(nullable.onLabel) },
        ]}
        onChange={(v) => {
          props.onChange(v === 'on' ? (typeof props.value === 'number' ? props.value : nullable.onValue) : null);
        }}
      />
      {!isNull && field}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Slider
// ---------------------------------------------------------------------------------------------------------------

const LOG_STEPS = 1000;

function SliderEditor(props: EditorViewProps & { readonly editor: Extract<RowEditor, { kind: 'slider' }> }) {
  const { editor, title, text } = props;
  const stored = typeof props.value === 'number' ? props.value : editor.min;
  const [live, setLive] = useState<number | null>(null);
  const toPos = (v: number): number =>
    editor.log === true
      ? Math.round((Math.log(Math.max(editor.min, v) / editor.min) / Math.log(editor.max / editor.min)) * LOG_STEPS)
      : v;
  const fromPos = (p: number): number => {
    if (editor.log !== true) return p;
    const v = editor.min * Math.pow(editor.max / editor.min, p / LOG_STEPS);
    const rounded = Math.round(v / editor.step) * editor.step;
    return Math.min(editor.max, Math.max(editor.min, Number(rounded.toFixed(4))));
  };
  const format = (pos: number): string => {
    const v = fromPos(pos);
    if (editor.format === 'percent') return text.t(m.percentValue, { value: Math.round(v * 100) });
    if (editor.format === 'times') return text.t(m.timesValue, { value: v.toFixed(2) });
    return String(v);
  };
  const shown = live ?? stored;
  return (
    <Slider
      className={s.slider}
      label={hidden(title)}
      value={toPos(shown)}
      min={editor.log === true ? 0 : editor.min}
      max={editor.log === true ? LOG_STEPS : editor.max}
      step={editor.log === true ? 1 : editor.step}
      format={format}
      onChange={(pos, final) => {
        const v = fromPos(pos);
        if (props.commitOnRelease === true && !final) {
          setLive(v);
          return;
        }
        setLive(null);
        if (!deepEqual(v, stored)) props.onChange(v);
      }}
    />
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Choice
// ---------------------------------------------------------------------------------------------------------------

function ChoiceEditor(props: EditorViewProps & { readonly editor: Extract<RowEditor, { kind: 'choice' }> }) {
  const { editor, title, text } = props;
  const options: readonly ChoiceOption[] = useMemo(() => {
    // A stored value that is not one of the choices (an older or imported setting) stays visible and selectable.
    if (editor.options.some((o) => deepEqual(o.value, props.value)) || props.value === undefined) return editor.options;
    const raw = typeof props.value === 'string' ? props.value : JSON.stringify(props.value);
    return [...editor.options, { value: props.value as Json, label: { raw } }];
  }, [editor.options, props.value]);
  const index = options.findIndex((o) => deepEqual(o.value, props.value));
  const current = index < 0 ? 'mixed' : String(index);
  const selected = index < 0 ? undefined : options[index];
  const items = options.map((o, i) => ({ value: String(i), label: optionText(text.t, o.label) }));
  const pick = (v: string): void => {
    const o = options[Number(v)];
    if (o !== undefined && !deepEqual(o.value, props.value)) props.onChange(o.value);
  };
  const longLabels = items.some((i) => i.label.length > 14);
  const segmented = editor.style !== 'select' && options.length <= 4 && !(longLabels && options.length > 3);
  return (
    <div className={s.choice}>
      {segmented ? (
        <Segmented<string> ariaLabel={title} value={current} options={items} onChange={pick} size="sm" />
      ) : (
        <Select<string> ariaLabel={title} value={current} options={items} onChange={pick} />
      )}
      {selected?.description !== undefined && (
        <p className={s.choiceDesc} aria-live="polite">
          {text.t(selected.description)}
        </p>
      )}
    </div>
  );
}
